import {DataTableColumn} from "mantine-datatable";
import {Invoice} from "../types.ts";
import {Anchor, Text} from '@mantine/core';
import {AiOutlineDownload} from 'react-icons/ai';
import {NavLink} from 'react-router-dom';

export const useTableColumns = (context: 'customer' | 'reports' = 'reports', onRecordClick?: (record: Invoice) => void) => {

    // FNP-324: in the standalone /invoices context, the id cell deep-links to
    // the detail page; in the embedded `customer` context (rendered inside the
    // customers detail page) it stays a click-to-drawer because there is no
    // route param available and the embedding screen owns the drawer.
    const columns: DataTableColumn<Invoice>[] = [
        {
            accessor: 'id',
            title: 'Invoice ID',
            render: (record: Invoice) =>
                context === 'reports' ? (
                    <NavLink to={`/invoices/${record.id}`}>{record.id}</NavLink>
                ) : (
                    <Text
                        c="blue"
                        style={{cursor: 'pointer'}}
                        onClick={() => onRecordClick?.(record)}
                    >
                        {record.id}
                    </Text>
                ),
        },
        ...(context === 'reports' ? [{
            accessor: 'customer',
            title: 'Customer',
            render: (record: Invoice) => (
                <Text c="blue" style={{cursor: 'pointer'}}>
                    {record.customer ? record.customer.name : record.customerId}
                </Text>
            )
        }] : []),
        {
            accessor: 'created',
            title: 'Created',
            render: (record: Invoice) => (
                <Text size="sm">
                    {record.created ? new Date(record.created).toLocaleDateString() : '-'}
                </Text>
            )
        },
        {
            // FNP-114: deep-link to the hosted invoice (Stripe's
            // hosted_invoice_url today, S3 presigned URL for non-Stripe
            // providers later). Renders an em-dash when no URL is populated
            // yet so the column doesn't mislead for invoices that pre-date
            // the field.
            accessor: 'invoiceUrl',
            title: 'Invoice',
            render: (record: Invoice) => record.invoiceUrl ? (
                <Anchor
                    href={record.invoiceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="sm"
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`View invoice ${record.id}`}
                >
                    <AiOutlineDownload size={14} style={{verticalAlign: 'middle', marginRight: 4}}/>
                    View
                </Anchor>
            ) : (
                <Text size="sm" c="dimmed">—</Text>
            ),
        },
    ];

    return {
        columns,
    }
}
